/**
 * Rooms data: the list, one room's messages, and the live wiring.
 *
 * Messages are held as one ordered array per room, keyed by their number in
 * that room. That number is what makes everything else simple: live arrivals
 * slot in, a reconnect asks for everything after the last one held, and the
 * unread count is a subtraction.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { useAuth } from "@/context/AuthContext";
import { onConnectionChange, onLiveEvent, type LiveEvent } from "@/lib/rooms/live";
import {
  getBlocks,
  getMessages,
  getUnreadCount,
  getRoom,
  getRooms,
  markRead,
  sendMessage,
  type RoomList,
  type RoomMessage,
} from "@/lib/services/rooms";

export const ROOM_KEYS = {
  list: ["rooms", "list"] as const,
  room: (id: string) => ["rooms", "room", id] as const,
  messages: (id: string) => ["rooms", "messages", id] as const,
  seenBy: (id: string) => ["rooms", "seen-by", id] as const,
};

/** A message that is on screen but not yet saved, or failed on the way. */
export interface PendingFlags {
  pending?: boolean;
  failed?: boolean;
}

export type ChatMessage = RoomMessage & PendingFlags;

function sortBySeq(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].sort((a, b) => {
    // A pending message has no number yet, so it waits at the end.
    if (a.seq === b.seq) return a.created_at < b.created_at ? -1 : 1;
    return a.seq - b.seq;
  });
}

/** Insert or replace, matching on the saved id first, then the client's own. */
function merge(existing: ChatMessage[], incoming: ChatMessage): ChatMessage[] {
  const index = existing.findIndex(
    (m) =>
      m.id === incoming.id ||
      (incoming.client_msg_id && m.client_msg_id === incoming.client_msg_id),
  );
  if (index === -1) return sortBySeq([...existing, incoming]);
  const copy = [...existing];
  copy[index] = { ...copy[index], ...incoming, pending: false, failed: false };
  return sortBySeq(copy);
}

export function useRoomList() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ROOM_KEYS.list,
    queryFn: getRooms,
    enabled: !!user,
    staleTime: 15_000,
  });
}

export function useRoom(roomId?: string) {
  return useQuery({
    queryKey: ROOM_KEYS.room(roomId ?? ""),
    queryFn: () => getRoom(roomId as string),
    enabled: !!roomId,
  });
}

export function useRoomMessages(roomId?: string) {
  const queryClient = useQueryClient();
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const query = useQuery({
    queryKey: ROOM_KEYS.messages(roomId ?? ""),
    queryFn: async () => {
      const page = await getMessages(roomId as string);
      setHasMore(page.has_more);
      return page.results as ChatMessage[];
    },
    enabled: !!roomId,
    staleTime: 10_000,
  });

  const loadOlder = useCallback(async () => {
    if (!roomId || loadingOlder) return;
    const held = queryClient.getQueryData<ChatMessage[]>(ROOM_KEYS.messages(roomId)) ?? [];
    const oldest = held.find((m) => m.seq > 0);
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const page = await getMessages(roomId, { before_seq: oldest.seq });
      setHasMore(page.has_more);
      queryClient.setQueryData<ChatMessage[]>(ROOM_KEYS.messages(roomId), (current) =>
        sortBySeq([...(page.results as ChatMessage[]), ...(current ?? [])]),
      );
    } finally {
      setLoadingOlder(false);
    }
  }, [queryClient, roomId, loadingOlder]);

  return { ...query, loadOlder, loadingOlder, hasMore };
}

export function useSendMessage(roomId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({ body, replyTo, clientMsgId, mediaIds }: {
      body: string; replyTo?: string; clientMsgId: string; mediaIds?: string[];
    }) =>
      sendMessage(roomId, {
        body,
        client_msg_id: clientMsgId,
        reply_to: replyTo,
        media_ids: mediaIds,
      }),

    // Show it at once, with a clock. The same id goes to the server, so a
    // retry on a bad connection cannot post the message twice.
    onMutate: async ({ body, clientMsgId, replyTo }) => {
      await queryClient.cancelQueries({ queryKey: ROOM_KEYS.messages(roomId) });
      const held = queryClient.getQueryData<ChatMessage[]>(ROOM_KEYS.messages(roomId)) ?? [];
      const optimistic: ChatMessage = {
        id: `pending-${clientMsgId}`,
        seq: Number.MAX_SAFE_INTEGER,
        room: roomId,
        kind: "text",
        body,
        client_msg_id: clientMsgId,
        reply_to: replyTo
          ? (held.find((m) => m.id === replyTo)?.reply_to ?? null)
          : null,
        is_deleted: false,
        is_hidden: false,
        created_at: new Date().toISOString(),
        author: user?.username ?? "You",
        author_handle_id: null,
        author_is_verified: false,
        author_profile_picture: null,
        is_mine: true,
        pending: true,
      };
      queryClient.setQueryData<ChatMessage[]>(ROOM_KEYS.messages(roomId), [...held, optimistic]);
      return { clientMsgId };
    },

    onSuccess: (saved) => {
      queryClient.setQueryData<ChatMessage[]>(ROOM_KEYS.messages(roomId), (current) =>
        merge(current ?? [], { ...saved, is_mine: true }),
      );
      queryClient.invalidateQueries({ queryKey: ROOM_KEYS.list });
    },

    onError: (_error, variables) => {
      queryClient.setQueryData<ChatMessage[]>(ROOM_KEYS.messages(roomId), (current) =>
        (current ?? []).map((m) =>
          m.client_msg_id === variables.clientMsgId ? { ...m, pending: false, failed: true } : m,
        ),
      );
    },
  });
}

export function useMarkRead(roomId?: string) {
  const queryClient = useQueryClient();
  const lastSent = useRef(0);

  return useCallback(
    (seq: number) => {
      if (!roomId || seq <= lastSent.current) return;
      lastSent.current = seq;
      markRead(roomId, seq)
        .then(() => {
          queryClient.setQueryData<RoomList>(ROOM_KEYS.list, (current) =>
            current
              ? {
                  ...current,
                  rooms: current.rooms.map((r) =>
                    r.id === roomId ? { ...r, unread_count: 0, last_read_seq: seq } : r,
                  ),
                }
              : current,
          );
          // The nav badge is its own query, so writing the room list does not
          // reach it. Without this a member who opened Rooms, read everything
          // and walked back to the feed carried the old number on the nav item
          // until some unrelated live event happened to arrive.
          queryClient.invalidateQueries({ queryKey: ROOMS_UNREAD_KEY });
        })
        .catch(() => {
          // Reading is not worth an error in someone's face; the next read retries.
          lastSent.current = 0;
        });
    },
    [queryClient, roomId],
  );
}

function applyLiveEvent(queryClient: QueryClient, event: LiveEvent) {
  if (event.type === "room.message" || event.type === "room.message_changed") {
    const message = event.message as ChatMessage;
    queryClient.setQueryData<ChatMessage[]>(ROOM_KEYS.messages(event.room), (current) =>
      current ? merge(current, message) : current,
    );
    queryClient.invalidateQueries({ queryKey: ROOM_KEYS.list });
    return;
  }
  if (event.type === "room.read") {
    queryClient.setQueryData(ROOM_KEYS.seenBy(event.room), { seq: event.seq, count: event.seen_by });
    return;
  }
  if (event.type === "room.membership") {
    queryClient.invalidateQueries({ queryKey: ROOM_KEYS.room(event.room) });
    queryClient.invalidateQueries({ queryKey: ROOM_KEYS.list });
  }
}

/**
 * Opens the live connection while Rooms is on screen, and keeps the cache in
 * step with it. Mount once, high up in the Rooms pages.
 */
export function useRoomsLive(activeRoomId?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [online, setOnline] = useState(false);

  useEffect(() => {
    if (!user) return;
    const stopEvents = onLiveEvent((event) => applyLiveEvent(queryClient, event));
    const stopStatus = onConnectionChange(setOnline);
    return () => {
      stopEvents();
      stopStatus();
    };
  }, [queryClient, user]);

  // Coming back from a long drop: anything older than the server's short
  // replay window is fetched the ordinary way.
  useEffect(() => {
    if (!online || !activeRoomId) return;
    const held = queryClient.getQueryData<ChatMessage[]>(ROOM_KEYS.messages(activeRoomId)) ?? [];
    const newest = held.filter((m) => !m.pending).reduce((max, m) => Math.max(max, m.seq), 0);
    if (!newest) return;
    getMessages(activeRoomId, { after_seq: newest }).then((page) => {
      if (!page.results.length) return;
      queryClient.setQueryData<ChatMessage[]>(ROOM_KEYS.messages(activeRoomId), (current) =>
        (page.results as ChatMessage[]).reduce((acc, m) => merge(acc, m), current ?? []),
      );
    });
  }, [online, activeRoomId, queryClient]);

  return { online };
}


/**
 * Who this member has chosen not to hear.
 *
 * The server already strips a blocked member's words out of history. This is
 * for the live push, which is one payload sent to the whole room and so cannot
 * know who blocked whom: the client drops those messages before drawing them.
 */
export function useBlockedHandles() {
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ["rooms", "blocked"],
    queryFn: getBlocks,
    enabled: !!user,
    staleTime: 60_000,
  });

  // One Set per answer, not one per render: the conversation keys its message
  // list off this, and a fresh Set every render redoes that work every render.
  const blockedHandleIds = useMemo(() => new Set(query.data?.handle_ids ?? []), [query.data]);

  return { blockedHandleIds, refreshBlocks: query.refetch };
}


export const ROOMS_UNREAD_KEY = ["rooms", "unread-count"] as const;

/**
 * The shortest gap between two badge requests, however many events arrive.
 *
 * The badge has an observer on every page, so an invalidation is an immediate
 * request. One per message meant that in a room of five hundred people, one
 * message was one request from every member's browser — the cost the live
 * connection exists to avoid, charged per message instead of per page, and
 * spent out of each reader's own rate limit. The first event still updates the
 * badge at once; a flood costs six requests a minute.
 */
const BADGE_REFRESH_MS = 10_000;

/**
 * The badge on the Rooms nav item.
 *
 * Asked for once when the app loads and then kept current by the live
 * connection, rather than polled: a poll on every page for every signed-in
 * member is the cost the live connection exists to avoid. Mounted in the
 * shell, so the badge is right wherever the member happens to be.
 */
export function useRoomsUnread() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const nextAllowed = useRef(0);
  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data } = useQuery({
    queryKey: ROOMS_UNREAD_KEY,
    queryFn: getUnreadCount,
    enabled: !!user,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!user) return;
    const stop = onLiveEvent((event) => {
      // A message in a room I am not reading right now is the only thing that
      // can raise the badge; reading is what lowers it, and `useMarkRead` asks
      // for the number again rather than guessing it. A read marker from
      // somebody else changes nothing here, so it is ignored.
      if (event.type !== "room.message" && event.type !== "room.membership") return;
      const now = Date.now();
      if (now >= nextAllowed.current) {
        nextAllowed.current = now + BADGE_REFRESH_MS;
        queryClient.invalidateQueries({ queryKey: ROOMS_UNREAD_KEY });
        return;
      }
      // Inside the window: one catch-up at the end of it, not one per event.
      if (trailing.current) return;
      trailing.current = setTimeout(() => {
        trailing.current = null;
        nextAllowed.current = Date.now() + BADGE_REFRESH_MS;
        queryClient.invalidateQueries({ queryKey: ROOMS_UNREAD_KEY });
      }, nextAllowed.current - now);
    });
    return () => {
      stop();
      if (trailing.current) {
        clearTimeout(trailing.current);
        trailing.current = null;
      }
    };
  }, [queryClient, user]);

  return { unread: data?.count ?? 0, roomsWithUnread: data?.rooms ?? 0 };
}
